import type { User } from "../types";
import type { AuthTokens } from "../auth/AuthRegistry";
import { apiClient } from "../apiClient";

// Define the shape of login/register credentials
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  name?: string;
}

// Result shape for login/register
export interface AuthResult {
  user: User;
  tokens: AuthTokens;
  region?: string | null;
}

/**
 * Auth API Service: All authentication-related backend queries.
 * All functions are async, pure, and return normalized data.
 */

// Live login implementation
export async function login(credentials: LoginCredentials): Promise<AuthResult> {
  try {
    // The backend expects "username", not "email"
    const payload = new URLSearchParams();
    payload.append("username", credentials.email);
    payload.append("password", credentials.password);

    const response = await apiClient.post(
      "/api/v1/auth/jwt/login",
      payload,
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );
    const accessToken = response.data.access_token;
    const region = response.data.region as string | undefined;
    const normalizedRegion = region && region !== "default" ? region : null;
    if (!accessToken) {
      throw new Error("Login failed: No access token received from server.");
    }
    // No user info returned; use placeholder user for now
    const user: User = {
      id: "unknown",
      email: credentials.email,
      name: credentials.email,
      roles: [],
    };
    return {
      user,
      tokens: {
        accessToken,
        refreshToken: null, // No refresh token in this flow
      },
      region: normalizedRegion,
    };
  } catch (error: any) {
    // Propagate error for UI to handle
    throw new Error(
      error?.response?.data?.detail ||
      error?.message ||
      "Login failed"
    );
  }
}

// Example: register
export async function register(credentials: RegisterCredentials): Promise<AuthResult> {
  // TODO: Replace with real API call
  return {
    user: {
      id: 2,
      email: credentials.email,
      name: credentials.name || "New User",
      roles: ["user"],
      avatar: undefined,
    },
    tokens: {
      accessToken: "mock-access-token",
      refreshToken: "mock-refresh-token",
    },
  };
}

// Example: refresh token
export async function refreshToken(refreshToken: string): Promise<AuthTokens> {
  // TODO: Replace with real API call
  return {
    accessToken: "new-mock-access-token",
    refreshToken: "new-mock-refresh-token",
  };
}
